/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  SkillValidationError,
  SkillValidationError$inboundSchema,
} from "./skillvalidationerror.js";

/**
 * An immutable version of a skill manifest.
 */
export type SkillVersion = {
  /**
   * The SHA-256 manifest digest derived from canonicalized SKILL.md content.
   */
  canonicalSha256: string;
  /**
   * The exact uploaded SKILL.md content.
   */
  content: string;
  /**
   * When this immutable version was recorded.
   */
  createdAt: Date;
  /**
   * The user that recorded this version.
   */
  createdByUserId: string;
  /**
   * The source version this version was derived from.
   */
  derivedFromVersionId?: string | undefined;
  /**
   * The optional description from this manifest version.
   */
  description?: string | undefined;
  /**
   * When this exact version was first activated.
   */
  firstSeenAt?: Date | undefined;
  /**
   * All top-level frontmatter fields parsed from this manifest version.
   */
  frontmatter: { [k: string]: any };
  /**
   * The skill version ID.
   */
  id: string;
  /**
   * When this exact version was most recently activated.
   */
  lastSeenAt?: Date | undefined;
  /**
   * Metadata parsed from this manifest version.
   */
  metadata: { [k: string]: any };
  /**
   * The SHA-256 digest of the exact uploaded SKILL.md content.
   */
  rawSha256: string;
  /**
   * The number of activations attributed to this exact version.
   */
  seenCount: number;
  /**
   * The skill that owns this version.
   */
  skillId: string;
  /**
   * Whether this manifest version conforms to the Agent Skills specification.
   */
  specValid: boolean;
  /**
   * Specification validation problems recorded for this manifest version.
   */
  validationErrors: Array<SkillValidationError>;
};

/** @internal */
export const SkillVersion$inboundSchema: z.ZodMiniType<SkillVersion, unknown> =
  z.pipe(
    z.object({
      canonical_sha256: z.string(),
      content: z.string(),
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      created_by_user_id: z.string(),
      derived_from_version_id: z.optional(z.string()),
      description: z.optional(z.string()),
      first_seen_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      frontmatter: z.record(z.string(), z.any()),
      id: z.string(),
      last_seen_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      metadata: z.record(z.string(), z.any()),
      raw_sha256: z.string(),
      seen_count: z.int(),
      skill_id: z.string(),
      spec_valid: z.boolean(),
      validation_errors: z.array(SkillValidationError$inboundSchema),
    }),
    z.transform((v) => {
      return remap$(v, {
        "canonical_sha256": "canonicalSha256",
        "created_at": "createdAt",
        "created_by_user_id": "createdByUserId",
        "derived_from_version_id": "derivedFromVersionId",
        "first_seen_at": "firstSeenAt",
        "last_seen_at": "lastSeenAt",
        "raw_sha256": "rawSha256",
        "seen_count": "seenCount",
        "skill_id": "skillId",
        "spec_valid": "specValid",
        "validation_errors": "validationErrors",
      });
    }),
  );

export function skillVersionFromJSON(
  jsonString: string,
): SafeParseResult<SkillVersion, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SkillVersion$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SkillVersion' from JSON`,
  );
}
